import context from '@/context'
import getComponentKey from '@/utils/getComponentKey'
import { allowErrorDialog } from '@/utils/fetch'
import { ReportSubmissionGroupState } from '@/enums/ReportSubmissionGroupState'
import { CheckingRuleResult } from '@/enums/checkingRuleResult'

const initialState = {
  signing: false,
  fetching: false,
  adding: false,
  removing: false,
  attachmentsKey: getComponentKey(),
  reportSubmissionGroup: null,
  reportUploadAdding: false,
  reportUploadRemoving: false,
  reportUploadApplying: false,
  reportUploadsKey: getComponentKey(),
  gridKey: getComponentKey(),
  pageKey: getComponentKey(),
  previousSubmissionGroupState: 0,
  statusCode: null
}

export const actionTypes = {
  reload: 'reportSubmissionGroup/reload',
  fetching: 'reportSubmissionGroup/fetching',
  fetched: 'reportSubmissionGroup/fetched',
  setGridKey: 'reportSubmissionGroup/gridKey',
  attachmentAdding: 'reportSubmissionGroup/attachment/adding',
  attachmentAdded: 'reportSubmissionGroup/attachment/added',
  attachmentRemoving: 'reportSubmissionGroup/attachment/removing',
  attachmentRemoved: 'reportSubmissionGroup/attachment/removed',
  submitting: 'reportSubmissionGroup/submitting',
  submitted: 'reportSubmissionGroup/submitted',
  submitFinished: 'reportSubmissionGroup/submitFinished',
  xmlSigning: 'reportSubmissionGroup/xmlSigning',
  stopXmlSigning: 'reportSubmissionGroup/stopXmlSigning',
  reportUploadAdding: 'reportSubmissionGroup/reportUpload/adding',
  reportUploadAdded: 'reportSubmissionGroup/reportUpload/added',
  reportUploadRemoving: 'reportSubmissionGroup/reportUpload/removing',
  reportUploadRemoved: 'reportSubmissionGroup/reportUpload/removed',
  reportUploadApplying: 'reportSubmissionGroup/reportUpload/applying',
  reportUploadApplied: 'reportSubmissionGroup/reportUpload/applied',
  setInstanceGroupState: 'reportSubmissionGroup/setInstanceGroupState',
  error: 'reportSubmissionGroup/error'
}

export const actionCreators = {
  reload: (id) => {
    return { type: actionTypes.reload, id: id }
  },
  get: (id) => (dispatch) => {
    dispatch({ type: actionTypes.fetching })
    context.api.reportSubmissionGroupService.get(id, allowErrorDialog)
      .then(response => {
        dispatch({ type: actionTypes.fetched, data: response })
      }).catch(() => dispatch({ type: actionTypes.error, update: { fetching: false } }))
  },
  addAttachments: (input) => (dispatch) => {
    dispatch({ type: actionTypes.attachmentAdding })
    context.api.reportSubmissionGroupService.addAttachments(input)
      .then(() => {
        dispatch({ type: actionTypes.attachmentAdded })
      }).catch(() => dispatch({ type: actionTypes.error, update: { adding: false } }))
  },
  removeAttachments: (input) => (dispatch) => {
    dispatch({ type: actionTypes.attachmentRemoving })
    context.api.reportSubmissionGroupService.removeAttachments(input)
      .then(() => {
        dispatch({ type: actionTypes.attachmentRemoved })
      }).catch(() => dispatch({ type: actionTypes.error, update: { removing: false } }))
  },
  submit: (id) => (dispatch) => {
    dispatch({ type: actionTypes.submitting })
    context.api.reportSubmissionGroupService.submit(id, allowErrorDialog)
      .catch(() => dispatch({ type: actionTypes.error, update: { submitting: false } }))
  },
  addReportUploads: (input) => (dispatch) => {
    dispatch({ type: actionTypes.reportUploadAdding })
    context.api.reportSubmissionGroupService.addReportUploads(input)
      .then(() => {
        dispatch({ type: actionTypes.reportUploadAdded })
      }).catch(() => dispatch({ type: actionTypes.error, update: { reportUploadAdding: false } }))
  },
  removeReportUploads: (input) => (dispatch) => {
    dispatch({ type: actionTypes.reportUploadRemoving })
    context.api.reportSubmissionGroupService.removeReportUploads(input)
      .then(() => {
        dispatch({ type: actionTypes.reportUploadRemoved })
      }).catch(() => dispatch({ type: actionTypes.error, update: { reportUploadRemoving: false } }))
  },
  applyReportUpload: (input) => (dispatch) => {
    dispatch({ type: actionTypes.reportUploadApplying })
    context.api.reportExportService.importReportTemplateFromExcelFile(input)
      .catch(() => dispatch({ type: actionTypes.reportUploadApplied }))
  },
  reportUploadApplied: () => (dispatch) => dispatch({ type: actionTypes.reportUploadApplied }),
  setSigning: () => (dispatch) => dispatch({ type: actionTypes.xmlSigning }),
  stopXmlSigning: () => (dispatch) => dispatch({ type: actionTypes.stopXmlSigning }),
  setGridKey: () => (dispatch) => dispatch({ type: actionTypes.setGridKey }),
  setInstanceGroupState: (state) => (dispatch) => dispatch({ type: actionTypes.setInstanceGroupState, update: state })
}

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.reload: {
      if (state.reportSubmissionGroup && state.reportSubmissionGroup.id === action.id) {
        return { ...state, pageKey: getComponentKey() }
      }
      return state
    }
    case actionTypes.fetching:
      return { ...state, fetching: true }
    case actionTypes.fetched:
      return {
        ...state,
        fetching: false,
        reportSubmissionGroup: action.data,
        signing: false,
        submitting: action.data.state === ReportSubmissionGroupState.enum.SignPending,
        statusCode: null
      }
    case actionTypes.setGridKey:
      return {
        ...state,
        gridKey: getComponentKey()
      }
    case actionTypes.attachmentAdding:
      return { ...state, adding: true }
    case actionTypes.attachmentAdded:
      return { ...state, adding: false, attachmentsKey: getComponentKey() }
    case actionTypes.attachmentRemoving:
      return { ...state, removing: true }
    case actionTypes.attachmentRemoved:
      return { ...state, removing: false, attachmentsKey: getComponentKey() }
    case actionTypes.submitting:
      return {
        ...state,
        reportSubmissionGroup: {
          ...state.reportSubmissionGroup,
          isBeingSubmitted: true
        }
      }
    case actionTypes.submitted:
      return {
        ...state,
        reportSubmissionGroup: {
          ...state.reportSubmissionGroup,
          isBeingSubmitted: false
        }
      }

    case actionTypes.reportUploadAdding:
      return { ...state, reportUploadAdding: true }
    case actionTypes.reportUploadAdded:
      return { ...state, reportUploadAdding: false, reportUploadsKey: getComponentKey() }
    case actionTypes.reportUploadRemoving: return { ...state, reportUploadRemoving: true }
    case actionTypes.reportUploadRemoved: return { ...state, reportUploadRemoving: false, reportUploadsKey: getComponentKey() }
    case actionTypes.reportUploadApplying: return { ...state, reportUploadApplying: true }
    case actionTypes.reportUploadApplied: return { ...state, reportUploadApplying: false }
    case actionTypes.error: return { ...state, ...action.update }
    case actionTypes.submitFinished:
      return {
        ...state,
        gridKey: getComponentKey(),
        reportSubmissionGroup: {
          ...state.reportSubmissionGroup,
          isBeingSubmitted: false,
          highestSeverity: action.highestSeverity,
          state: action.highestSeverity === CheckingRuleResult.enum.Succeeded
            ? ReportSubmissionGroupState.enum.SignPending : state.reportSubmissionGroup.state
        }
      }
    case actionTypes.xmlSigning:
      return { ...state, signing: true }
    case actionTypes.stopXmlSigning:
      return { ...state, signing: false }
    case actionTypes.setInstanceGroupState:
      return (!!state.reportSubmissionGroup && action.update !== state.reportSubmissionGroup.processState)
        ? { ...state, reportSubmissionGroup: { ...state.reportSubmissionGroup, processState: action.update } }
        : state

    default: return state
  }
}



// WEBPACK FOOTER //
// ./src/store/ReportSubmissionGroup.js